/**
 * Session storage utility for persisting form respondent sessions.
 * Uses localStorage by default with 24-hour expiration.
 */

const SESSION_TTL_HOURS = 24;

export interface StoredSession {
  responseId: string;
  sessionToken: string;
  projectId: string;
  formId: string;
  version: number;
  createdAt: string; // ISO timestamp
}

function getStorageKey(
  projectId: string,
  formId: string,
  version: number,
): string {
  return `kushya_session_${projectId}_${formId}_${version}`;
}

function isSessionExpired(createdAt: string): boolean {
  const created = new Date(createdAt);
  const now = new Date();
  const hoursDiff = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
  return hoursDiff > SESSION_TTL_HOURS;
}

/**
 * Get stored session for a form if it exists and is not expired.
 */
export function getSession(
  projectId: string,
  formId: string,
  version: number,
): StoredSession | null {
  try {
    const key = getStorageKey(projectId, formId, version);
    const data = localStorage.getItem(key);

    if (!data) {
      return null;
    }

    const session = JSON.parse(data) as StoredSession;

    // Check expiration
    if (isSessionExpired(session.createdAt)) {
      clearSession(projectId, formId, version);
      return null;
    }

    // Validate session data matches params
    if (
      session.projectId !== projectId ||
      session.formId !== formId ||
      session.version !== version
    ) {
      clearSession(projectId, formId, version);
      return null;
    }

    return session;
  } catch {
    // Corrupted data or localStorage unavailable
    clearSession(projectId, formId, version);
    return null;
  }
}

/**
 * Store a session for a form.
 */
export function setSession(session: StoredSession): void {
  try {
    const key = getStorageKey(
      session.projectId,
      session.formId,
      session.version,
    );
    localStorage.setItem(key, JSON.stringify(session));
  } catch {
    // localStorage unavailable or quota exceeded - silently fail
    console.warn("Failed to save session to localStorage");
  }
}

/**
 * Clear stored session for a form.
 */
export function clearSession(
  projectId: string,
  formId: string,
  version: number,
): void {
  try {
    const key = getStorageKey(projectId, formId, version);
    localStorage.removeItem(key);
  } catch {
    // localStorage unavailable - silently fail
  }
}

/**
 * Clear all Kushya sessions from localStorage.
 */
export function clearAllSessions(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("kushya_session_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // localStorage unavailable - silently fail
  }
}

export const sessionStorage = {
  get: getSession,
  set: setSession,
  clear: clearSession,
  clearAll: clearAllSessions,
};
